// packages/shared/src/entitlements.ts
//
// Single source of truth for the pricing-tier -> feature matrix, consumed by
// BOTH the middleware server resolver (`services/entitlements.ts`) and the React
// `EntitlementsProvider`. FE and BE can never disagree about the matrix because
// there is exactly one copy — this file (DESIGN-NOTES §1, AC-9).
//
// Matrix source of truth: docs/plan/2026-07-08-pricing-tier-gating.md §3.2 +
// docs/plan/2026-07-02-pricing-tiers.html (Rev 3, 2026-07-02). Tiers are built
// as strict supersets: essentials ⊂ signature ⊂ reserve.

export type PlanTier = 'essentials' | 'signature' | 'reserve';

export type EntitlementKey =
  // Signature fences (the "operating tier")
  | 'whatsapp' | 'deposits' | 'noShowRiskScoring' | 'events'
  | 'customFields' | 'analyticsFull' | 'reputation'
  | 'messagingInbox'
  // Signature fences added by #805 (Rob's 2026-09-16 placements, parent #760·m).
  // Key name === flag name except `menuEditor`, whose flag is the hyphenated
  // catalog key `menu-editor` (precedent: `reports` → `analyticsFull`).
  | 'recurringExperiences' | 'cardHolds' | 'waitlistRecovery' | 'notifyRefill'
  | 'menuEditor' | 'deepCrm' | 'spatialFloorPlan' | 'pacingPlanner'
  // `configVersioning` was the ninth; RULING R24 removed it — see SIGNATURE_FENCES.
  // Reserve fences (AI suite + revenue/integration)
  | 'aiGuestIntelligence' | 'aiShiftBriefing' | 'aiBookingAssistant'
  | 'aiNoShowPrediction' | 'aiPredictiveDemand'
  | 'dynamicDeposits' | 'posIntegration'
  // Reserve fence added by #806 (parent #760·m). ONE key, TWO ops flags — see
  // FLAG_ENTITLEMENT below; `ordering` is deliberately NOT an AddOnKey.
  | 'ordering'
  // Add-ons (any tier; the AI four are included in Reserve)
  | 'crmNewsletter' | 'socialPlanner' | 'voiceReservations' | 'voiceOrdering'
  | 'winbackAutomation';

export type AddOnKey =
  | 'aiGuestIntelligence' | 'aiBookingAssistant' | 'aiNoShowPrediction'
  | 'aiPredictiveDemand'  // the four à-la-carte AI products (included in Reserve)
  | 'crmNewsletter' | 'socialPlanner'
  | 'voiceReservations' | 'voiceOrdering' // per-minute COGS — separate even on Reserve
  | 'winbackAutomation'; // #805 — messaging COGS; à la carte on EVERY tier incl. Reserve

// ── Tier fences (composed as supersets) ─────────────────────────────────────

// Signature adds the operating-tier fences on top of Essentials (all-core).
const SIGNATURE_FENCES: EntitlementKey[] = [
  'whatsapp',
  'messagingInbox',
  'deposits',
  'noShowRiskScoring',
  'events',
  'customFields',
  'analyticsFull',
  'reputation',
  // ── #805 (Rob, 2026-09-16; parent #760·m) ───────────────────────────────
  // Eight capabilities (nine as shipped; R24 below removed one) that were
  // flag-only with no fence. Each is IMPLEMENTED
  // (ADR-2026-09-16-TIER-MATRIX-TRUTH: a key may only sit here if something
  // ships it) — the reader of each is named beside it. These are UI/plan-chip
  // fences today: no `requireFeature()` route call was added in #805, so the
  // per-capability SERVER fence remains an open decision.
  'recurringExperiences', // routes/experiences-api.ts (RECURRING_EXPERIENCES_FLAG)
  'cardHolds',            // routes/deposits-api.ts `cardHoldsEnabled`
  'waitlistRecovery',     // routes/waitlist-api.ts + features/waitlist/WaitlistPanel.jsx
  'notifyRefill',         // routes/waitlist-api.ts notify/offer + reservations-api cancel match
  'menuEditor',           // flag `menu-editor` — features/settings/groups/RestaurantSettings.jsx
  'deepCrm',              // routes/contacts-preferences-api.ts segments/spend + GuestsPage
  'spatialFloorPlan',     // features/floor-plan/FloorPlanPage.jsx spatial column
  'pacingPlanner',        // app/routes.jsx → features/planner/PlannerPage.jsx
  // ── RULING R24 (Rob, 2026-09-23) — DO NOT RE-ADD `configVersioning` ──────
  // #805 placed it here as the ninth Signature fence. R24 reverses that ONE
  // placement, and only it: the settings change history + rollback is the
  // AUDIT TRAIL that makes venue-owned settings safe to hand to a venue at all
  // (rulings R21-R23 on #541 gave venues their own config write). A safety net
  // is not a paid upsell — fencing it would mean the tenants on the cheapest
  // plan, who can least afford a bad settings save, are the ones with no way
  // back. So it stays a plain ops FEATURE FLAG with no tier fence and no
  // FLAG_ENTITLEMENT mapping (the absence is pinned three ways in
  // __tests__/entitlements.test.ts, plus the resolver pin in the middleware
  // suite and the label pin in src/features/subscription/tierAccess.test.js).
  // Re-adding it takes a NEW ruling from Rob, not a tidy-up.
  // #770 (Rob, 2026-09-17) — `widgetBrandingRemoval` REMOVED under the same rule
  // as ADR-2026-09-16-TIER-MATRIX-TRUTH: nothing implements it. "Powered by
  // YeboGuest" is hardcoded at packages/widget/src/components/book-now-button.ts
  // :367 and :416 with no config-driven conditional, and PUBLIC_WIDGET_CONFIG_KEYS
  // carries no suppression key. Re-add it in the same change that ships it.
];

// Reserve adds the AI suite + revenue/integration fences on top of Signature.
//
// ADR-2026-09-16-TIER-MATRIX-TRUTH (ruling R1, Rob 2026-09-16): a key belongs in
// this list ONLY if something implements it. `aiEodReport`, `publicApi` and
// `advancedRoles` were removed on 2026-09-16 — each existed only in this file and
// the compiled `dist/`, with zero implementation in `packages/` or `src/`, so
// Reserve (R2,499/mo) advertised three capabilities that did not exist. Add a key
// back only in the same change that ships the code behind it.
//
// `aiNoShowPrediction` is the known fourth case and stays deliberately. Nothing
// implements it distinctly — the no-show radar (`/api/reservations/no-show-radar`)
// is fenced on the SIGNATURE key `noShowRiskScoring`, and `/api/no-show-risk` is
// an ungated heuristic — but it is also an `AddOnKey` carrying live Stripe prices
// (`STRIPE_PRICE_ADDON_AI_NO_SHOW_PREDICTION_{ZA,GLOBAL}`, both stages), and the
// shared suite pins `ADDON_CATALOG.includedInReserve` to Reserve membership. So
// dropping it strands two minted prices and is a commercial call, not a code one.
const RESERVE_FENCES: EntitlementKey[] = [
  'aiGuestIntelligence',
  'aiShiftBriefing',
  'aiBookingAssistant',
  'aiNoShowPrediction',
  'aiPredictiveDemand',
  'dynamicDeposits',
  'posIntegration',
  // ── #806 (parent #760·m) ───────────────────────────────────────
  // In-table ordering + the kitchen display, sold as ONE Reserve capability.
  // Matrix-truth (ADR-2026-09-16-TIER-MATRIX-TRUTH) is satisfied twice over —
  // BOTH ops flags have live readers:
  //   `in-table-ordering` → app/routes.jsx:231 (/order/:locationId/:tableId)
  //                       + app/routes.jsx:284 (/orders, staff)
  //   `kds-view`          → app/routes.jsx:281 (/kitchen, the KDS board)
  // Server side the surfaces exist too (routes/orders.ts, routes/kitchen.ts on
  // fnApiOrders), but like #805 this is a UI/plan-chip fence ONLY: #806 adds NO
  // `requireFeature('ordering')` route call. Those routes are LIVE with a real
  // customer (Pilot POS), so the server fence is a separate, reviewed change.
  'ordering',
];

export const TIER_ENTITLEMENTS: Record<PlanTier, EntitlementKey[]> = {
  essentials: [],
  signature: [...SIGNATURE_FENCES],
  reserve: [...SIGNATURE_FENCES, ...RESERVE_FENCES],
};

// ── Add-on catalog ───────────────────────────────────────────────────────────
// `includedInReserve` MUST agree with the Reserve tier fence for every add-on
// (invariant pinned by the shared suite). The four à-la-carte AI products are
// bundled into Reserve; CRM/Newsletter, Social planner, and the two Voice
// products carry marginal COGS and stay separate on every tier incl. Reserve.
export const ADDON_CATALOG: Record<AddOnKey, { includedInReserve: boolean }> = {
  aiGuestIntelligence: { includedInReserve: true },
  aiBookingAssistant: { includedInReserve: true },
  aiNoShowPrediction: { includedInReserve: true },
  aiPredictiveDemand: { includedInReserve: true },
  crmNewsletter: { includedInReserve: false },
  socialPlanner: { includedInReserve: false },
  voiceReservations: { includedInReserve: false },
  voiceOrdering: { includedInReserve: false },
  // #805 (Rob, 2026-09-16) — win-back campaigns (lambda/winback-scheduler.ts +
  // services/messaging/winback.ts) are an ADD-ON on every tier, Reserve
  // included: each campaign run is outbound messaging COGS. No price is set
  // here — this catalog carries membership only (`includedInReserve`); the
  // Stripe price ids live in services/pricing-catalog.ts, and no
  // `STRIPE_PRICE_ADDON_WINBACK_*` has been minted yet (commercial call, Rob).
  winbackAutomation: { includedInReserve: false },
};

// ── Seat limits per tier ───────────────────────────────────────────────────
export const SEAT_LIMITS: Record<PlanTier, number | null> = {
  essentials: 3,
  signature: 10,
  reserve: null, // unlimited
};

// ── Core flags — NEVER tier-fenced (the "never fence" list) ─────────────────
// Feature-flag keys that every tier keeps regardless of plan: base operations,
// waitlist basics, email confirmations, guest self-service, and the POPIA
// compliance surfaces (export/erasure — DR-PRIVACY-01, must never be fenced).
export const CORE_FLAGS: string[] = [
  'reservations',
  // #805 (Rob, 2026-09-16) — live board updates are formally NEVER fenceable:
  // a stale board is a wrong board on every plan (reader: RealtimeProvider.jsx).
  'realtime',
  'month',
  'tables',
  'overview',
  'settings',
  'staff',
  'waitlist',
  'emailConfirmations',
  'guestSelfService',
  'popiaExport',
  'popiaErasure',
  'csvImport',
];

// ── Flag -> entitlement mapping (only for flags that ARE tier-fenced) ────────
// Maps a config feature-flag key (ops on/off toggle) to the entitlement key the
// tier gate checks. A route is allowed iff the flag is on AND the resolved
// entitlement set contains the mapped key (DESIGN-NOTES §2.2). Flags with no
// entry here are flag-only (no tier fence) and keep the plain flag check.
export const FLAG_ENTITLEMENT: Record<string, EntitlementKey> = {
  deposits: 'deposits',
  events: 'events',
  whatsapp: 'whatsapp',
  messagingInbox: 'messagingInbox',
  reputation: 'reputation',
  reports: 'analyticsFull',
  customFields: 'customFields',
  crmNewsletter: 'crmNewsletter',
  // #805 — the 2026-09-16 Signature placements, EIGHT after ruling R24 dropped
  // `configVersioning`. Flag name === entitlement key for seven of them;
  // `menu-editor` is hyphenated in the flag catalog, so its mapping key is the
  // literal flag and its entitlement is `menuEditor` (same shape as
  // `reports` → `analyticsFull`).
  recurringExperiences: 'recurringExperiences',
  cardHolds: 'cardHolds',
  waitlistRecovery: 'waitlistRecovery',
  notifyRefill: 'notifyRefill',
  'menu-editor': 'menuEditor',
  deepCrm: 'deepCrm',
  spatialFloorPlan: 'spatialFloorPlan',
  pacingPlanner: 'pacingPlanner',
  // `configVersioning:` REMOVED by ruling R24 (Rob, 2026-09-23) — history is
  // for everyone, so the flag carries no tier fence. See SIGNATURE_FENCES.
  // #806 — THE FIRST MANY-TO-ONE MAPPING. Two ops flags, one Reserve fence.
  // Declaration order is LOAD-BEARING (see ENTITLEMENT_FLAG below): the PARENT
  // capability is declared first, because `kds-view` dependsOn "Guest ordering
  // (QR)" in src/features/settings/featureCatalog.js — there are no kitchen
  // tickets without guest orders. NB the bare `ordering` key in the FE's
  // DEFAULT_KNOWN_FLAGS is a different thing: an advertised ops flag with no
  // reader (#393, reserved name) and deliberately NO entry here.
  'in-table-ordering': 'ordering',
  'kds-view': 'ordering',
  // Hyphenated LITERAL catalog keys (match FE DEFAULT_KNOWN_FLAGS). Coordinator
  // resolution 2026-07-08 (slice-6 §7-a): fixed from camelCase; neither is on a
  // live requireFeature fence, so behaviour-neutral.
  'voice-reservations': 'voiceReservations',
  'voice-ordering': 'voiceOrdering',
};

// ── Entitlement -> ops-flag reverse lookup (slice 3) ─────────────────────────
// The inverse of FLAG_ENTITLEMENT: given an entitlement key, which config
// feature-flag key is its ops on/off switch. Only entries where the flag name
// DIFFERS from the entitlement key matter (`analyticsFull` → `reports`,
// `menuEditor` → `menu-editor`, `ordering` → `in-table-ordering`);
// `requireFeature` falls back to the entitlement key itself when there is no
// entry (identity), which is correct for e.g. `noShowRiskScoring`.
//
// THE MANY-TO-ONE RULE (#806). FLAG_ENTITLEMENT stopped being a bijection when
// `in-table-ordering` and `kds-view` both mapped to `ordering`. This map stays
// ONE-TO-ONE — its single consumer, `middleware/require-feature.ts:123`, asks
// for exactly one flag ("which ops switch must be on") — and the rule is:
//
//   FIRST DECLARED FLAG WINS. The canonical flag for an entitlement is the
//   first one listed for it in FLAG_ENTITLEMENT, which by convention is the
//   PARENT capability. So `ENTITLEMENT_FLAG.ordering === 'in-table-ordering'`.
//
// (The loop below therefore skips a key it has already filled. That is a change
// of RULE, not of behaviour: before #806 every entitlement had exactly one flag,
// so last-wins and first-wins produced identical output — pinned by a test.)
//
// CONSEQUENCE, stated so nobody trips over it: a future `requireFeature(...,
// 'ordering')` on a KITCHEN route would check `in-table-ordering`, not
// `kds-view`, and 404 for a tenant who runs the KDS with guest ordering off.
// #806 adds no such fence. When one is added, pass the route's own flag
// explicitly or read the full set off FLAG_ENTITLEMENT — do not assume this map
// names the right switch for every route an entitlement covers.
export const ENTITLEMENT_FLAG: Partial<Record<EntitlementKey, string>> = (() => {
  const rev: Partial<Record<EntitlementKey, string>> = {};
  for (const [flag, ent] of Object.entries(FLAG_ENTITLEMENT)) {
    if (rev[ent] === undefined) rev[ent] = flag; // first declared wins
  }
  return rev;
})();

// Tier ordering low → high, so `requiredTierFor` returns the LOWEST granting tier.
const TIER_ORDER: PlanTier[] = ['essentials', 'signature', 'reserve'];

/**
 * The lowest `PlanTier` whose entitlement set grants `key`, or `undefined` when
 * no tier grants it (à-la-carte add-on keys like `crmNewsletter`/voice, or a
 * CORE flag that is not an entitlement at all). Drives the `requireFeature` 402
 * `requiredTier` (AC-22) and the tier-vs-add-on denial split (coordinator
 * resolution #3): a `undefined` result means "not tier-fenced" — the caller
 * emits `ADDON_REQUIRED` for a known add-on, otherwise no tier fence applies.
 */
export function requiredTierFor(key: EntitlementKey): PlanTier | undefined {
  for (const tier of TIER_ORDER) {
    if (TIER_ENTITLEMENTS[tier].includes(key)) return tier;
  }
  return undefined;
}
